/**
 * BankReportSlides - Comprehensive Business Report Presentation
 * Version 2.0 - Full Business Exposé
 * 
 * Displays interactive slide presentation with:
 * - Property Overview
 * - Owners Structure
 * - Extended Financial Metrics
 * - Units & Tenants Table (ALL units, ALL tenants)
 * - Rent Distribution Charts
 * - Cashflow Analysis
 * - Risk Analysis
 * - Market Insights
 * - Summary & Recommendation
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Maximize2, 
  Minimize2,
  Download,
  Building,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  Info,
  Target,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import EmailShareButton from './BankReportInteractive/controls/EmailShareButton';
import ExportButton from './BankReportInteractive/controls/ExportButton';
import SlideRating from './BankReportInteractive/slides/SlideRating';

// Register ChartJS components
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function BankReportSlides({ slides, propertyName, reportData, propertyId, userId, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return <TitleSlide slide={slide} />;
      case 'property_overview':
        return <PropertyOverviewSlide slide={slide} />;
      case 'owners_table':
        return <OwnersTableSlide slide={slide} />;
      case 'metrics_extended':
        return <MetricsExtendedSlide slide={slide} />;
      case 'units_tenants_table':
        return <UnitsTenantsTableSlide slide={slide} />;
      case 'chart':
        return <ChartSlide slide={slide} />;
      case 'cashflow':
        return <CashflowSlide slide={slide} />;
      case 'risk_analysis':
        return <RiskAnalysisSlide slide={slide} />;
      case 'rent_potential':
        return <RentPotentialSlide slide={slide} />;
      case 'financing_structure':
        return <FinancingStructureSlide slide={slide} />;
      case 'debt_service':
        return <DebtServiceSlide slide={slide} />;
      case 'market_insights':
        return <MarketInsightsSlide slide={slide} />;
      case 'summary_recommendation':
        return <SummaryRecommendationSlide slide={slide} />;
      case 'metrics':
        return <MetricsSlide slide={slide} />;
      case 'insight':
        return <InsightSlide slide={slide} />;
      case 'summary':
        return <SummarySlide slide={slide} />;
      case 'table':
        return <GenericTableSlide slide={slide} />;
      case 'vpi_potential':
        return <RentPotentialSlide slide={slide} />;
      case 'owner_info':
        return <OwnersTableSlide slide={slide} />;
      case 'property_details':
        return <PropertyOverviewSlide slide={slide} />;
      case 'cashflow_analysis':
        return <CashflowSlide slide={slide} />;
      case 'vacancy_risks':
        return <RiskAnalysisSlide slide={slide} />;
      case 'property_valuation':
        return <PropertyValuationSlide slide={slide} />;
      case 'rating':
        return (
          <SlideRating
            rating={slide.rating}
            ratingLabel={slide.ratingLabel}
            summary={slide.summary}
            recommendations={slide.recommendations}
            criteria={slide.criteria}
          />
        );
      case 'units_grid':
        return <UnitsGridSlide slide={slide} />;
      default:
        return <div className="text-white">Unknown slide type: {slide.type}</div>;
    }
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
    >
      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
        <div className="text-white">
          <h2 className="text-lg font-semibold">{propertyName}</h2>
          <p className="text-sm text-gray-300">Betriebswirtschaftliches Exposé</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export PDF Button */}
          {reportData && (
            <ExportButton
              reportData={reportData}
              reportId={reportData?.id}
              deckRef={containerRef}
            />
          )}
          
          {/* Email Share Button */}
          {reportData && propertyId && userId && (
            <EmailShareButton
              reportData={reportData}
              propertyId={propertyId}
              propertyName={propertyName}
              userId={userId}
              deckRef={containerRef}
            />
          )}
          
          <button
            onClick={toggleFullscreen}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Slide Content */}
      <div className="h-full flex items-center justify-center p-16">
        <div className="slide-container w-full max-w-7xl h-full">
          {slides.map((slide, index) => (
            <div
              key={index}
              className="w-full h-full flex items-center justify-center"
              style={{ display: index === currentSlide ? 'flex' : 'none' }}
            >
              {renderSlide(slide)}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-t from-black/50 to-transparent">
        {/* Previous Button */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors text-white"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Zurück</span>
        </button>

        {/* Slide Indicators */}
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-white w-8' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                title={`Slide ${index + 1}`}
              />
            ))}
          </div>
          <span className="text-white text-sm ml-2">
            {currentSlide + 1} / {slides.length}
          </span>
        </div>

        {/* Next Button */}
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors text-white"
        >
          <span className="hidden sm:inline">Weiter</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SLIDE COMPONENTS
// ============================================================================

// Title Slide
function TitleSlide({ slide }) {
  return (
    <div className="text-center text-white space-y-6">
      <div className="inline-block p-4 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl mb-6">
        <Building className="w-20 h-20 text-blue-400" />
      </div>
      <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
        {slide.title}
      </h1>
      <p className="text-xl text-gray-300">{slide.subtitle}</p>
      <div className="mt-12 space-y-3">
        <p className="text-3xl font-semibold text-blue-300">{slide.propertyName}</p>
        <p className="text-lg text-gray-400">{slide.location}</p>
        <p className="text-sm text-gray-500">{slide.date}</p>
      </div>
    </div>
  );
}

// Property Overview Slide
function PropertyOverviewSlide({ slide }) {
  // Support both formats: details array (Gemini) or data object (legacy)
  const items = slide.details ? slide.details : (slide.data ? Object.entries(slide.data).map(([key, value]) => ({ label: key.replace(/([A-Z])/g, ' $1').trim(), value })) : []);
  
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-6 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6 text-sm">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              {item.label}
            </p>
            <p className="text-lg font-semibold">{item.value}</p>
          </div>
        ))}
      </div>
      
      {slide.condition && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">Zustand: <span className="text-green-400 font-semibold">{slide.condition}</span></p>
        </div>
      )}
    </div>
  );
}

// Owners Table Slide
function OwnersTableSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6">{slide.subtitle}</p>}
      
      <div className="max-w-5xl mx-auto overflow-auto max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="bg-white/10 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Anteil</th>
              <th className="px-4 py-3 text-left">E-Mail</th>
              <th className="px-4 py-3 text-left">Telefon</th>
              {slide.owners[0]?.address && <th className="px-4 py-3 text-left">Adresse</th>}
            </tr>
          </thead>
          <tbody>
            {(slide.owners || []).map((owner, index) => (
              <tr key={index} className="border-b border-white/10 hover:bg-white/5">
                <td className="px-4 py-3 font-medium">{owner.name}</td>
                <td className="px-4 py-3">{owner.share}</td>
                <td className="px-4 py-3 text-gray-300">{owner.email}</td>
                <td className="px-4 py-3 text-gray-300">{owner.phone}</td>
                {owner.address && <td className="px-4 py-3 text-gray-300 text-xs">{owner.address}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Extended Metrics Slide
function MetricsExtendedSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6 text-sm">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {(slide.sections || []).map((section, idx) => (
          <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10">
            <h3 className="text-lg font-semibold mb-4 text-blue-300">{section.title}</h3>
            <div className="space-y-3">
              {section.metrics.map((metric, midx) => (
                <div key={midx} className="border-b border-white/10 pb-3 last:border-0">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-xs text-gray-400">{metric.label}</span>
                    <span className="text-lg font-bold">{metric.value}</span>
                  </div>
                  {metric.detail && (
                    <p className="text-xs text-gray-500">{metric.detail}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Generic Table Slide (for Gemini-generated tables)
function GenericTableSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-2xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-4 text-sm">{slide.subtitle}</p>}
      
      <div className="max-w-6xl mx-auto overflow-auto max-h-[600px]">
        <table className="w-full text-xs">
          <thead className="bg-white/10 sticky top-0">
            <tr>
              {(slide.headers || slide.columns || []).map((header, idx) => (
                <th key={idx} className="px-3 py-2 text-left font-semibold">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(slide.rows || []).map((row, rowIndex) => (
              <tr 
                key={rowIndex} 
                className="border-b border-white/10 hover:bg-white/5"
              >
                {Array.isArray(row) ? (
                  // Array format (from Gemini)
                  row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 text-gray-300">{cell}</td>
                  ))
                ) : (
                  // Object format (legacy)
                  Object.values(row).map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 text-gray-300">{cell}</td>
                  ))
                )}
              </tr>
            ))}
          </tbody>
          {slide.summary && (
            <tfoot className="bg-blue-500/20 border-t-2 border-blue-500/50">
              <tr className="font-bold text-white">
                {slide.summary.values ? (
                  // Array format (from Gemini)
                  slide.summary.values.map((val, idx) => (
                    <td key={idx} className="px-3 py-3">{val || '-'}</td>
                  ))
                ) : (
                  // Object format (legacy)
                  <td className="px-3 py-3" colSpan={(slide.headers || slide.columns || []).length}>
                    {slide.summary.label || 'Summe'}
                  </td>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// Units & Tenants Table Slide (COMBINED TABLE)
function UnitsTenantsTableSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-2xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-4 text-sm">{slide.subtitle}</p>}
      
      <div className="max-w-6xl mx-auto overflow-auto max-h-[600px]">
        <table className="w-full text-xs">
          <thead className="bg-white/10 sticky top-0">
            <tr>
              {slide.columns.map((col, idx) => (
                <th key={idx} className="px-3 py-2 text-left font-semibold">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slide.rows.map((row, index) => (
              <tr 
                key={index} 
                className={`border-b border-white/10 hover:bg-white/5 ${
                  row.status === 'Leerstand' ? 'bg-red-500/10' : ''
                }`}
              >
                <td className="px-3 py-2 font-medium">{row.unit}</td>
                <td className="px-3 py-2 text-gray-300">{row.size}</td>
                <td className="px-3 py-2 text-gray-300">{row.tenant}</td>
                <td className="px-3 py-2 text-gray-300">{row.contractEnd}</td>
                <td className="px-3 py-2 text-green-400 font-semibold">{row.coldRent}</td>
                <td className="px-3 py-2 text-gray-400">{row.utilities}</td>
                <td className="px-3 py-2 text-blue-400 font-semibold">{row.total}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    row.status === 'Vermietet' 
                      ? 'bg-green-500/20 text-green-300' 
                      : 'bg-red-500/20 text-red-300'
                  }`}>
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          {slide.summary && (
            <tfoot className="bg-blue-500/20 border-t-2 border-blue-500/50">
              <tr className="font-bold text-white">
                <td className="px-3 py-3">SUMME</td>
                <td className="px-3 py-3">{slide.summary?.totalSize || 'N/A'}</td>
                <td className="px-3 py-3">{slide.summary?.occupiedUnits || 0}/{slide.summary?.totalUnits || 0}</td>
                <td className="px-3 py-3">-</td>
                <td className="px-3 py-3 text-green-400">{slide.summary?.totalColdRent || 'N/A'}</td>
                <td className="px-3 py-3">{slide.summary?.totalUtilities || 'N/A'}</td>
                <td className="px-3 py-3 text-blue-400">{slide.summary?.totalRent || 'N/A'}</td>
                <td className="px-3 py-3">
                  <span className="text-xs">{slide.summary?.occupancyRate || 'N/A'}</span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      
      <div className="mt-4 text-center text-xs text-gray-500">
        Gesamt: {slide.rows.length} Einheiten | Vermietet: {slide.summary?.occupiedUnits || 0} | Leer: {(slide.summary?.totalUnits || 0) - (slide.summary?.occupiedUnits || 0)}
      </div>
    </div>
  );
}

// Chart Slide (Pie or Bar)
function ChartSlide({ slide }) {
  // Backend sends chartConfig with standard Chart.js format
  // chartConfig.data.datasets[0].data contains the values
  const chartData = {
    labels: slide.chartConfig?.data?.labels || [],
    datasets: slide.chartConfig?.data?.datasets || [{
      label: slide.chartConfig?.datasets?.[0]?.label || 'Data',
      data: slide.chartConfig?.data?.datasets?.[0]?.data || [],
      backgroundColor: slide.chartConfig?.data?.datasets?.[0]?.backgroundColor || [],
      borderWidth: 0
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#fff',
          font: { size: 11 },
          boxWidth: 15,
          padding: 10
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1
      }
    },
    ...(slide.chartType === 'bar' && {
      scales: {
        y: {
          ticks: { color: '#fff', font: { size: 10 } },
          grid: { color: 'rgba(255,255,255,0.1)' }
        },
        x: {
          ticks: { color: '#fff', font: { size: 10 } },
          grid: { display: false }
        }
      }
    })
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6">{slide.subtitle}</p>}
      
      <div className="max-w-4xl mx-auto" style={{ height: '500px' }}>
        {slide.chartType === 'pie' ? (
          <Pie data={chartData} options={options} />
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}

// Cashflow Slide
function CashflowSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* Income */}
        <div className="bg-green-500/10 backdrop-blur-sm rounded-lg p-6 border border-green-500/30">
          <h3 className="text-xl font-semibold mb-4 text-green-300 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Einnahmen
          </h3>
          <div className="space-y-3">
            {(slide.income?.items || []).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-300">{item.label}</span>
                <span className="font-semibold">{item.value}</span>
              </div>
            ))}
            <div className="pt-3 border-t border-green-500/30 flex justify-between text-lg font-bold">
              <span>Gesamt</span>
              <span className="text-green-400">{slide.income?.total || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Expenses */}
        <div className="bg-red-500/10 backdrop-blur-sm rounded-lg p-6 border border-red-500/30">
          <h3 className="text-xl font-semibold mb-4 text-red-300 flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Ausgaben
          </h3>
          <div className="space-y-3">
            {(slide.expenses?.items || []).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-300">{item.label}</span>
                <span className="font-semibold">{item.value}</span>
              </div>
            ))}
            <div className="pt-3 border-t border-red-500/30 flex justify-between text-lg font-bold">
              <span>Gesamt</span>
              <span className="text-red-400">{slide.expenses?.total || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Cashflow */}
      <div className="mt-6 max-w-5xl mx-auto bg-blue-500/10 backdrop-blur-sm rounded-lg p-6 border border-blue-500/30">
        <div className="flex justify-between items-center">
          <span className="text-xl font-semibold text-blue-300">Netto-Cashflow</span>
          <span className="text-3xl font-bold text-blue-400">{slide.netCashflow}</span>
        </div>
        {slide.cashflowMargin && (
          <p className="text-sm text-gray-400 mt-2">Cashflow-Marge: {slide.cashflowMargin}</p>
        )}
      </div>
    </div>
  );
}

// Risk Analysis Slide
function RiskAnalysisSlide({ slide }) {
  const getRiskColor = (level) => {
    switch (level.toLowerCase()) {
      case 'niedrig': return 'text-green-400 bg-green-500/20 border-green-500/50';
      case 'mittel': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      case 'hoch': return 'text-red-400 bg-red-500/20 border-red-500/50';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/50';
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {(slide.risks || []).map((risk, idx) => (
          <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold">{risk.category}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRiskColor(risk.level)}`}>
                {risk.level}
              </span>
            </div>
            <p className="text-sm text-gray-300 mb-2">{risk.description}</p>
            <p className="text-xs text-gray-500 italic">{risk.impact}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Market Insights Slide
function MarketInsightsSlide({ slide }) {
  const getInsightIcon = (type) => {
    switch (type) {
      case 'strength': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'risk': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      case 'opportunity': return <Target className="w-5 h-5 text-blue-400" />;
      default: return <Info className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6">{slide.subtitle}</p>}
      
      {/* Market Comparison */}
      {slide.marketComparison && (
        <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10 max-w-4xl mx-auto mb-6">
          <h3 className="text-lg font-semibold mb-4 text-blue-300">Marktvergleich</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {Object.entries(slide.marketComparison).map(([key, value]) => (
              <div key={key}>
                <p className="text-xs text-gray-400 mb-1">{key}</p>
                <p className="font-semibold">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {(slide.insights || []).map((insight, idx) => (
          <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 flex items-start gap-3">
            {getInsightIcon(insight.type)}
            <p className="text-sm text-gray-300 flex-1">{insight.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Summary & Recommendation Slide
function SummaryRecommendationSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      
      {/* Rating */}
      <div className="text-center mb-6">
        <div className="inline-block bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg px-6 py-3 border border-blue-500/30">
          <p className="text-sm text-gray-400 mb-1">Gesamtbewertung</p>
          <p className="text-3xl font-bold text-blue-300">{slide.rating?.label || slide.rating || 'N/A'}</p>
          {slide.rating?.score && <p className="text-lg text-gray-400 mt-1">{slide.rating.score}/10</p>}
        </div>
      </div>

      {/* Summary/Content */}
      {slide.summary && (
        <div className="bg-blue-500/10 backdrop-blur-sm rounded-lg p-4 border border-blue-500/30 max-w-4xl mx-auto mb-4">
          <h3 className="text-base font-semibold mb-2 text-blue-300">Zusammenfassung</h3>
          <p className="text-xs leading-relaxed text-gray-300 whitespace-pre-wrap">{slide.summary}</p>
        </div>
      )}

      {/* Recommendations */}
      {slide.recommendations && slide.recommendations.length > 0 && (
        <div className="bg-purple-500/10 backdrop-blur-sm rounded-lg p-4 border border-purple-500/30 max-w-4xl mx-auto mb-4">
          <h3 className="text-base font-semibold mb-3 text-purple-300 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Empfehlungen
          </h3>
          <ul className="space-y-1.5 text-xs">
            {slide.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">•</span>
                <span className="text-gray-300 leading-relaxed">{rec.text || rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Content (if no summary) */}
      {!slide.summary && slide.content && (
        <div className="bg-gray-500/10 backdrop-blur-sm rounded-lg p-5 border border-gray-500/30 max-w-5xl mx-auto">
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{slide.content}</p>
        </div>
      )}
    </div>
  );
}

// Legacy Metrics Slide (for backward compatibility)
function MetricsSlide({ slide }) {
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'positive': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'negative': return <TrendingDown className="w-4 h-4 text-red-400" />;
      case 'stable': return <Minus className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-4xl font-bold mb-8 text-center">{slide.title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
        {(slide.metrics || []).map((metric, index) => (
          <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <p className="text-sm text-gray-400 mb-2">{metric.label}</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold">{metric.value}</p>
              {getTrendIcon(metric.trend)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Legacy Insight Slide (for backward compatibility)
function InsightSlide({ slide }) {
  const getInsightIcon = (type) => {
    switch (type) {
      case 'strength': return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'risk': return <AlertTriangle className="w-6 h-6 text-red-400" />;
      case 'opportunity': return <Target className="w-6 h-6 text-blue-400" />;
      default: return <Info className="w-6 h-6 text-gray-400" />;
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-6 text-center">{slide.title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {slide.insights.map((insight, index) => (
          <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 flex items-start gap-3">
            {getInsightIcon(insight.type)}
            <p className="text-xs leading-relaxed text-gray-300 flex-1">{insight.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Rent Potential Slide
function RentPotentialSlide({ slide }) {
  // Check if we have new format with VPI and Mietspiegel comparison
  const hasComparison = slide.vpi && slide.mietspiegel;
  
  if (hasComparison) {
    // New format: VPI vs Mietspiegel comparison
    const vpiHigher = slide.recommended === 'vpi';
    
    return (
      <div className="w-full text-white p-6">
        <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
        {slide.subtitle && <p className="text-gray-400 text-center mb-8 text-sm">{slide.subtitle}</p>}
        
        {/* Comparison Summary */}
        <div className="max-w-6xl mx-auto mb-8 bg-gradient-to-r from-blue-500/20 to-green-500/20 rounded-xl p-6 border border-white/10">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-sm text-gray-400 mb-2">Empfohlene Methode</p>
              <p className="text-2xl font-bold text-yellow-400">{slide.comparison?.betterMethod || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-2">Maximales Potenzial</p>
              <p className="text-2xl font-bold text-green-400">{vpiHigher ? slide.vpi.yearlyPotential : slide.mietspiegel.yearlyPotential}</p>
              <p className="text-xs text-gray-500 mt-1">Pro Jahr</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-2">Differenz</p>
              <p className="text-xl font-bold text-purple-400">{slide.comparison?.difference || 'N/A'}</p>
            </div>
          </div>
        </div>
        
        {/* Two-column comparison: VPI vs Mietspiegel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          
          {/* VPI Column */}
          <div className={`rounded-xl p-6 border-2 ${vpiHigher ? 'bg-blue-500/20 border-blue-500' : 'bg-blue-500/10 border-blue-500/30'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-blue-300">📊 VPI (Verbraucherpreisindex)</h3>
              {vpiHigher && <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-500/50">Empfohlen</span>}
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-400">Aktueller Index</p>
                <p className="text-lg font-bold">{slide.vpi.currentIndex}</p>
                <p className="text-xs text-gray-500">{slide.vpi.period}</p>
              </div>
              <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
                <p className="text-xs text-green-300">Jahrespotenzial</p>
                <p className="text-2xl font-bold text-green-400">{slide.vpi.yearlyPotential}</p>
                <p className="text-xs text-gray-400 mt-1">Monatlich: {slide.vpi.monthlyPotential}</p>
              </div>
              <div className="bg-purple-500/20 rounded-lg p-3 border border-purple-500/30">
                <p className="text-xs text-purple-300">Steigerung</p>
                <p className="text-xl font-bold text-purple-400">{slide.vpi.percentIncrease}</p>
              </div>
            </div>
            
            {slide.vpi.adjustments && slide.vpi.adjustments.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 text-blue-200">Anpassungen pro Einheit</h4>
                <div className="overflow-auto max-h-64 bg-black/20 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-white/10 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Einheit</th>
                        <th className="px-2 py-2 text-right">Erhöhung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slide.vpi.adjustments.slice(0, 5).map((adj, idx) => (
                        <tr key={idx} className="border-b border-white/5">
                          <td className="px-2 py-2">{adj.unit}</td>
                          <td className="px-2 py-2 text-right text-green-400 font-semibold">{adj.increase}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          {/* Mietspiegel Column */}
          <div className={`rounded-xl p-6 border-2 ${!vpiHigher ? 'bg-green-500/20 border-green-500' : 'bg-green-500/10 border-green-500/30'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-green-300">🏘️ Mietspiegel</h3>
              {!vpiHigher && <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-500/50">Empfohlen</span>}
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-400">Quelle</p>
                <p className="text-sm font-semibold">{slide.mietspiegel.source}</p>
                <p className="text-xs text-gray-500">{slide.mietspiegel.validPeriod}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-400">Durchschnittsmiete</p>
                <p className="text-lg font-bold">{slide.mietspiegel.averageRent}</p>
                <p className="text-xs text-gray-500">Spanne: {slide.mietspiegel.range}</p>
              </div>
              <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
                <p className="text-xs text-green-300">Jahrespotenzial</p>
                <p className="text-2xl font-bold text-green-400">{slide.mietspiegel.yearlyPotential}</p>
                <p className="text-xs text-gray-400 mt-1">Monatlich: {slide.mietspiegel.monthlyPotential}</p>
              </div>
              <div className="bg-yellow-500/20 rounded-lg p-3 border border-yellow-500/30">
                <p className="text-xs text-yellow-300">Max. Erhöhung</p>
                <p className="text-sm font-bold text-yellow-400">{slide.mietspiegel.maxIncrease}</p>
              </div>
            </div>
            
            {slide.mietspiegel.adjustments && slide.mietspiegel.adjustments.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 text-green-200">Anpassungen pro Einheit</h4>
                <div className="overflow-auto max-h-64 bg-black/20 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-white/10 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Einheit</th>
                        <th className="px-2 py-2 text-right">Erhöhung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slide.mietspiegel.adjustments.slice(0, 5).map((adj, idx) => (
                        <tr key={idx} className="border-b border-white/5">
                          <td className="px-2 py-2">{adj.unit}</td>
                          <td className="px-2 py-2 text-right text-green-400 font-semibold">{adj.increase}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Legal Notice */}
        <div className="max-w-6xl mx-auto mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-xs text-yellow-300">
            <strong>Hinweis:</strong> Mieterhöhungen nach Mietspiegel sind auf max. 20% in 3 Jahren begrenzt (§ 558 BGB). 
            VPI-Anpassungen gelten nur bei entsprechender Vertragsklausel. Rechtliche Prüfung empfohlen.
          </p>
        </div>
      </div>
    );
  }
  
  // Legacy format: VPI only (backward compatibility)
  const monthlyPotential = slide.totalPotential || slide.potential?.totalPotential || 'N/A';
  const yearlyPotential = slide.yearlyPotential || slide.potential?.yearlyPotential || 'N/A';
  const currentVPI = slide.currentVPI || slide.vpiData?.currentIndex || 'N/A';
  const vpiPeriod = slide.period || 'Aktuell';
  
  return (
    <div className="w-full text-white">
      <h2 className="text-2xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6 text-sm">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-6">
        <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
          <h3 className="text-sm text-blue-300 mb-2">Aktueller VPI</h3>
          <p className="text-2xl font-bold">{currentVPI}</p>
          <p className="text-xs text-gray-400 mt-1">{vpiPeriod}</p>
        </div>
        <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
          <h3 className="text-sm text-green-300 mb-2">Monatliches Potenzial</h3>
          <p className="text-2xl font-bold text-green-400">{monthlyPotential}</p>
          <p className="text-xs text-gray-400 mt-1">Pro Monat</p>
        </div>
        <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
          <h3 className="text-sm text-purple-300 mb-2">Jahrespotenzial</h3>
          <p className="text-2xl font-bold text-purple-400">{yearlyPotential}</p>
          <p className="text-xs text-gray-400 mt-1">Pro Jahr</p>
        </div>
      </div>

      {slide.tenantAdjustments && slide.tenantAdjustments.length > 0 && (
        <div className="max-w-5xl mx-auto">
          <h3 className="text-lg font-semibold mb-3">Mietanpassungen pro Einheit</h3>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-white/10 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Einheit</th>
                  <th className="px-3 py-2 text-right">Aktuell</th>
                  <th className="px-3 py-2 text-right">Potenzial</th>
                  <th className="px-3 py-2 text-right">Steigerung</th>
                </tr>
              </thead>
              <tbody>
                {(slide.tenantAdjustments || []).map((adj, idx) => (
                  <tr key={idx} className="border-b border-white/10">
                    <td className="px-3 py-2">{adj.unit || 'N/A'}</td>
                    <td className="px-3 py-2 text-right">{adj.current || adj.currentRent || 'N/A'}</td>
                    <td className="px-3 py-2 text-right text-green-400 font-semibold">{adj.potential || adj.adjustedRent || 'N/A'}</td>
                    <td className="px-3 py-2 text-right text-green-300">{adj.increase || adj.percentChange || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Financing Structure Slide
function FinancingStructureSlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-2xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6 text-sm">{slide.subtitle}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto mb-6">
        <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
          <h3 className="text-sm text-red-300 mb-2">Gesamtverschuldung</h3>
          <p className="text-3xl font-bold text-red-400">{slide.totalDebt}</p>
        </div>
        <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
          <h3 className="text-sm text-yellow-300 mb-2">Loan-to-Value (LTV)</h3>
          <p className="text-3xl font-bold text-yellow-400">{slide.loanToValue}</p>
        </div>
      </div>

      {slide.financings && slide.financings.length > 0 && (
        <div className="max-w-5xl mx-auto mb-6">
          <h3 className="text-lg font-semibold mb-3">Darlehen</h3>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-white/10 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Kreditgeber</th>
                  <th className="px-3 py-2 text-right">Betrag</th>
                  <th className="px-3 py-2 text-right">Zinssatz</th>
                  <th className="px-3 py-2 text-right">Monatl. Rate</th>
                  <th className="px-3 py-2 text-left">Restlaufzeit</th>
                  <th className="px-3 py-2 text-left">Typ</th>
                </tr>
              </thead>
              <tbody>
                {(slide.financings || []).map((fin, idx) => (
                  <tr key={idx} className="border-b border-white/10">
                    <td className="px-3 py-2 font-medium">{fin.lender}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fin.loanAmount || fin.amount}</td>
                    <td className="px-3 py-2 text-right text-yellow-400">{fin.interestRate}</td>
                    <td className="px-3 py-2 text-right">{fin.monthlyInterest || fin.monthlyPayment}</td>
                    <td className="px-3 py-2">{fin.remainingTerm}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{fin.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {slide.summary && (
        <div className="bg-white/5 rounded-lg p-4 max-w-5xl mx-auto">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-400 mb-1">Monatl. Kapitaldienst</p>
              <p className="text-lg font-bold">{slide.summary?.totalMonthlyDebt || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400 mb-1">Jährl. Kapitaldienst</p>
              <p className="text-lg font-bold">{slide.summary?.totalYearlyDebt || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400 mb-1">Ø Zinssatz</p>
              <p className="text-lg font-bold text-yellow-400">{slide.summary?.averageInterestRate || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Debt Service Coverage Ratio Slide
function DebtServiceSlide({ slide }) {
  const getDSCRColor = (value) => {
    const dscr = parseFloat(value);
    if (dscr >= 1.25) return 'text-green-400 bg-green-500/20 border-green-500/50';
    if (dscr >= 1.0) return 'text-blue-400 bg-blue-500/20 border-blue-500/50';
    if (dscr >= 0.8) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
    return 'text-red-400 bg-red-500/20 border-red-500/50';
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-2xl font-bold mb-2 text-center">{slide.title}</h2>
      {slide.subtitle && <p className="text-gray-400 text-center mb-6 text-sm">{slide.subtitle}</p>}
      
      {/* DSCR Score */}
      <div className="text-center mb-6">
        <div className={`inline-block rounded-lg px-8 py-4 border-2 ${getDSCRColor(slide.dscr?.value || '0')}`}>
          <p className="text-sm text-gray-400 mb-1">Debt Service Coverage Ratio</p>
          <p className="text-5xl font-bold">{slide.dscr?.value || 'N/A'}</p>
          <p className="text-lg mt-2">{slide.dscr?.rating || 'N/A'}</p>
        </div>
        <p className="text-xs text-gray-500 mt-3 max-w-2xl mx-auto">{slide.dscr?.interpretation || ''}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* Income */}
        <div className="bg-green-500/10 rounded-lg p-5 border border-green-500/30">
          <h3 className="text-lg font-semibold mb-4 text-green-300">Einnahmen</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Bruttomiete</span>
              <span className="font-semibold">{slide.income?.grossRent || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Betriebskosten</span>
              <span className="font-semibold">-{slide.income?.operatingExpenses || 'N/A'}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-green-500/30 text-lg font-bold">
              <span>NOI</span>
              <span className="text-green-400">{slide.income?.netOperatingIncome || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Debt Service */}
        <div className="bg-red-500/10 rounded-lg p-5 border border-red-500/30">
          <h3 className="text-lg font-semibold mb-4 text-red-300">Kapitaldienst</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Jährlich</span>
              <span className="font-semibold">{slide.debt?.annualDebtService || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Monatlich</span>
              <span className="font-semibold">{slide.debt?.monthlyDebtService || 'N/A'}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-red-500/30 text-lg font-bold">
              <span>Gesamt/Jahr</span>
              <span className="text-red-400">{slide.debt?.annualDebtService || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Free Cashflow */}
      <div className="bg-blue-500/10 rounded-lg p-4 max-w-5xl mx-auto mt-6 border border-blue-500/30">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-blue-300">Freier Cashflow</span>
          <span className="text-3xl font-bold text-blue-400">{slide.cashflow?.freeCashflow || slide.freeCashflow || 'N/A'}</span>
        </div>
      </div>
    </div>
  );
}

// Legacy Summary Slide (for backward compatibility)
function SummarySlide({ slide }) {
  return (
    <div className="w-full text-white">
      <h2 className="text-4xl font-bold mb-8 text-center">{slide.title}</h2>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white/5 backdrop-blur-sm rounded-lg p-8 border border-white/10">
          <h3 className="text-xl font-semibold mb-4 text-blue-300">Key Takeaways</h3>
          <ul className="space-y-3">
            {(slide.takeaways || []).map((takeaway, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-lg text-gray-300">{takeaway}</span>
              </li>
            ))}
          </ul>
        </div>
        {slide.cta && (
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg p-8 border border-blue-500/30 text-center">
            <h3 className="text-2xl font-semibold mb-2">{slide.cta?.title || 'Kontakt'}</h3>
            <p className="text-gray-300">{slide.cta?.description || ''}</p>
          </div>
        )}
      </div>
    </div>
  );
}



// Property Valuation Slide (Ertragswertverfahren)
function PropertyValuationSlide({ slide }) {
  const { calculation, comparison, usedValue } = slide;
  
  return (
    <div className="w-full text-white">
      <h2 className="text-3xl font-bold mb-2 text-center">{slide.title}</h2>
      <p className="text-center text-gray-400 mb-6">{slide.subtitle}</p>
      
      {/* Calculation Steps */}
      <div className="max-w-4xl mx-auto space-y-4 mb-6">
        {/* NOI */}
        <div className="bg-blue-500/10 backdrop-blur-sm rounded-lg p-4 border border-blue-500/30">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-blue-300">{calculation?.noi?.label}</h3>
            <span className="text-2xl font-bold text-blue-300">{calculation?.noi?.value}</span>
          </div>
          <p className="text-sm text-gray-400">{calculation?.noi?.description}</p>
        </div>
        
        {/* Cap Rate */}
        <div className="bg-purple-500/10 backdrop-blur-sm rounded-lg p-4 border border-purple-500/30">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-purple-300">{calculation?.capRate?.label}</h3>
            <div className="text-right">
              <span className="text-2xl font-bold text-purple-300">{calculation?.capRate?.value}</span>
              <p className="text-sm text-purple-400">{calculation?.capRate?.category}</p>
            </div>
          </div>
          <p className="text-sm text-gray-400">{calculation?.capRate?.description}</p>
        </div>
        
        {/* Income Value */}
        <div className="bg-green-500/10 backdrop-blur-sm rounded-lg p-4 border border-green-500/30">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-green-300">{calculation?.incomeValue?.label}</h3>
            <span className="text-2xl font-bold text-green-300">{calculation?.incomeValue?.value}</span>
          </div>
          <p className="text-sm text-gray-400 mb-2">{calculation?.incomeValue?.description}</p>
          <p className="text-xs text-green-400 font-mono">Formel: {calculation?.incomeValue?.formula}</p>
        </div>
      </div>
      
      {/* Comparison with Purchase Price */}
      {comparison?.purchasePrice !== '0,00 €' && comparison?.purchasePrice !== 'N/A' && (
        <div className="max-w-4xl mx-auto bg-gray-500/10 backdrop-blur-sm rounded-lg p-4 border border-gray-500/30 mb-6">
          <h3 className="text-base font-semibold text-gray-300 mb-3">Vergleich mit Kaufpreis</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Ertragswert</p>
              <p className="text-xl font-bold text-white">{comparison?.incomeValue}</p>
            </div>
            <div>
              <p className="text-gray-400">Kaufpreis</p>
              <p className="text-xl font-bold text-white">{comparison?.purchasePrice}</p>
            </div>
            <div>
              <p className="text-gray-400">Differenz</p>
              <p className="text-lg font-semibold text-white">{comparison?.difference}</p>
            </div>
            <div>
              <p className="text-gray-400">Differenz %</p>
              <p className="text-lg font-semibold text-white">{comparison?.differencePercent}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Used Value for LTV */}
      <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg p-4 border border-blue-500/30 text-center">
        <p className="text-sm text-gray-400 mb-1">Für LTV-Berechnung verwendet</p>
        <p className="text-2xl font-bold text-blue-300">{usedValue?.value}</p>
        <p className="text-xs text-gray-400 mt-1">Methode: {usedValue?.method}</p>
      </div>
    </div>
  );
}



/**
 * UnitsGridSlide Component
 * Displays units in a compact 2x2 grid with all essential information
 */
function UnitsGridSlide({ slide }) {
  const { units = [], summary = {}, aiSummary } = slide;
  
  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 text-white">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-1">{slide.title}</h2>
        <p className="text-gray-400 text-sm">{slide.subtitle}</p>
      </div>
      
      {/* Units Grid - Compact 2x2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 flex-1 overflow-auto">
        {units.map((unit, index) => (
          <UnitCard key={index} unit={unit} />
        ))}
      </div>
      
      {/* AI Summary */}
      {aiSummary && (
        <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-2xl">🤖</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-purple-300 mb-1">KI-Analyse</p>
              <p className="text-xs text-gray-300 leading-relaxed">{aiSummary}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Summary Bar */}
      <div className="mt-4 grid grid-cols-4 gap-3 text-center text-xs">
        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
          <div className="text-gray-400 mb-1">Auslastung</div>
          <div className="font-bold text-green-400">{summary.occupancyRate}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
          <div className="text-gray-400 mb-1">Gesamtfläche</div>
          <div className="font-bold">{summary.totalSize}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
          <div className="text-gray-400 mb-1">Gesamt-Miete</div>
          <div className="font-bold text-blue-400">{summary.totalRent}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
          <div className="text-gray-400 mb-1">Ø Mietdauer</div>
          <div className="font-bold">{summary.avgLeaseDuration}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * UnitCard Component - Compact design
 */
/**
 * UnitCard Component - Enhanced with MORE information
 * - Gewerbe/Wohnen distinction
 * - VAT display for commercial units
 * - More compact layout with all details
 */
function UnitCard({ unit }) {
  const isVacant = unit.status === 'vacant';
  const isCommercial = unit.is_commercial;
  
  return (
    <div className={`
      relative rounded-xl p-4 border-2 transition-all duration-300
      ${isVacant 
        ? 'bg-red-500/5 border-red-500/30 hover:border-red-500/50' 
        : isCommercial
          ? 'bg-blue-500/5 border-blue-500/30 hover:border-blue-500/50'
          : 'bg-green-500/5 border-green-500/30 hover:border-green-500/50'
      }
      hover:shadow-lg hover:scale-[1.02]
    `}>
      {/* Header: Unit Name + Size + Type + Status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs">{isCommercial ? '🏢' : '🏠'}</span>
            <h3 className="font-bold text-sm leading-tight">{unit.unitName}</h3>
            {isCommercial && (
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded border border-blue-500/50">
                GEWERBE
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            {unit.size} {unit.rooms && `• ${unit.rooms} Zi.`}
          </div>
        </div>
        <div className={`
          px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap
          ${isVacant 
            ? 'bg-red-500/20 text-red-300 border border-red-500/50' 
            : 'bg-green-500/20 text-green-300 border border-green-500/50'
          }
        `}>
          {isVacant ? '🔺 LEER' : '✓'}
        </div>
      </div>
      
      {/* Tenant Info */}
      <div className="mb-3 pb-2 border-b border-white/10">
        <div className="text-xs text-gray-500 mb-0.5">Mieter</div>
        <div className="text-sm font-medium">
          {isVacant ? (
            <span className="text-red-400">
              {unit.vacancyDays ? `Leerstand seit ${unit.vacancyDays} Tagen` : 'Leer'}
            </span>
          ) : (
            <span>{unit.tenantName || 'N/A'}</span>
          )}
        </div>
      </div>
      
      {/* Financial Details - 2 columns */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs mb-2">
        <div>
          <div className="text-gray-500">Kaltmiete</div>
          <div className="font-semibold">{unit.coldRent}</div>
        </div>
        <div>
          <div className="text-gray-500">Nebenkosten</div>
          <div className="font-semibold">{unit.utilities}</div>
        </div>
        
        {/* VAT for commercial units */}
        {isCommercial && unit.vat_amount && !isVacant && (
          <>
            <div className="col-span-2">
              <div className="text-gray-500">+ 19% MwSt</div>
              <div className="font-semibold text-blue-300">{unit.vat_amount}</div>
            </div>
          </>
        )}
      </div>
      
      {/* Total Rent - Highlighted */}
      <div className="mb-3 p-2 bg-white/5 rounded-lg border border-white/10">
        <div className="text-xs text-gray-400 mb-0.5">
          Warmmiete gesamt {isCommercial && !isVacant ? '(inkl. MwSt)' : ''}
        </div>
        <div className="text-lg font-bold text-green-400">{unit.totalRent}</div>
      </div>
      
      {/* Additional Info - Compact badges */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {unit.rentPerSqm && !isVacant && (
          <span className="px-2 py-1 bg-purple-500/10 text-purple-300 rounded border border-purple-500/30">
            {unit.rentPerSqm}
          </span>
        )}
        {unit.deposit && !isVacant && (
          <span className="px-2 py-1 bg-yellow-500/10 text-yellow-300 rounded border border-yellow-500/30">
            💰 {unit.deposit}
          </span>
        )}
        {unit.daysUntilEnd && unit.daysUntilEnd < 180 && !isVacant && (
          <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded border border-red-500/50 font-bold">
            ⚠️ Endet in {unit.daysUntilEnd}T
          </span>
        )}
      </div>
      
      {/* Lease Info */}
      {!isVacant && (
        <div className="mt-3 pt-2 border-t border-white/10 grid grid-cols-2 gap-2 text-[10px]">
          <div>
            <div className="text-gray-500">Vertragsbeginn</div>
            <div className="font-medium">{unit.leaseStart || '-'}</div>
          </div>
          <div>
            <div className="text-gray-500">Mietdauer</div>
            <div className="font-medium">{unit.leaseDuration || '- Jahre'}</div>
          </div>
          {unit.lastRentIncrease && (
            <div className="col-span-2">
              <div className="text-gray-500">Letzte Anpassung</div>
              <div className="font-medium">{unit.lastRentIncrease}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


